import {
  Building2,
  EyeOff,
  FileWarning,
  FlaskConical,
  PauseCircle,
  Receipt,
  ShieldCheck,
  Trash2,
  UserX,
} from 'lucide-react';
import type { AdminOrganizationSummary } from '@/client/types/admin/users-orgs';
import { Stat } from '@/client/components/common/StatsGrid';

/**
 * Summary strip for the org list.
 *
 * Do NOT lead with active/inactive — `active` is effectively unused (most rows
 * are NULL). Prefer deleted / hold / review / testers as operational filters
 * per the admin org guide §4. Orphans use the `has_owner=0` list filter.
 */
export function buildOrganizationStats(
  summary: AdminOrganizationSummary | null | undefined
): Stat[] {
  const total = summary?.total_organizations ?? 0;
  const share = (n?: number) =>
    total > 0 && n !== undefined
      ? `${((n / total) * 100).toFixed(1)}% of ${total}`
      : undefined;

  return [
    {
      key: 'total',
      label: 'Total',
      value: summary?.total_organizations,
      icon: Building2,
      tone: 'bg-blue-50 text-blue-600',
    },
    {
      key: 'orphans',
      label: 'No owner',
      value: undefined,
      hint: 'has_owner=0',
      icon: UserX,
      tone: 'bg-amber-50 text-amber-700',
    },
    {
      key: 'testers',
      label: 'Test orgs',
      value: summary?.tester_organizations,
      hint: share(summary?.tester_organizations),
      icon: FlaskConical,
      tone: 'bg-fuchsia-50 text-fuchsia-700',
    },
    {
      key: 'held',
      label: 'On hold',
      value: summary?.held_organizations,
      hint: share(summary?.held_organizations),
      icon: PauseCircle,
      tone: 'bg-red-50 text-red-600',
    },
    {
      key: 'review',
      label: 'Under review',
      value: summary?.under_review_organizations,
      hint: share(summary?.under_review_organizations),
      icon: FileWarning,
      tone: 'bg-amber-50 text-amber-600',
    },
    {
      key: 'deleted',
      label: 'Deleted',
      value: summary?.deleted_organizations,
      hint: share(summary?.deleted_organizations),
      icon: Trash2,
      tone: 'bg-rose-50 text-rose-600',
    },
    {
      key: 'undiscoverable',
      label: 'Undiscoverable',
      value: summary?.undiscoverable_organizations,
      hint: share(summary?.undiscoverable_organizations),
      icon: EyeOff,
      tone: 'bg-gray-100 text-gray-600',
    },
    {
      key: 'verified',
      label: 'Verified',
      value: summary?.verified_organizations,
      hint: share(summary?.verified_organizations),
      icon: ShieldCheck,
      tone: 'bg-blue-50 text-blue-600',
    },
    {
      key: 'vat',
      label: 'VAT registered',
      value: summary?.vat_registered_organizations,
      hint: share(summary?.vat_registered_organizations),
      icon: Receipt,
      tone: 'bg-violet-50 text-violet-600',
    },
  ];
}
